import { useQuery, useMutation } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import styled, { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import DateTimePicker from 'components/DateTimePicker';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  getHighestItemOrderNumber,
  getOrderedItems,
} from 'components/Form/DraggableFields';
import { Select as FormFieldSelect } from 'components/FormFields';
import AttachmentUpload from 'components/FormFields/AttachmentUpload';
import CategorySelect from 'components/FormFields/CategorySelect';
import ColorSelect from 'components/FormFields/ColorSelect';
import SearchableSelect from 'components/FormFields/SearchableSelect';
import Toggle from 'components/FormFields/Toggle';
import Toaster from 'components/Toaster';
import Tooltip from 'components/Tooltip';
import { COLOR_CODES } from 'pages/app/Events/EventEdit/EditEventTickets';
import { GET_WAIVERS } from 'pages/app/Events/EventEdit/WaiversRepeater/Waivers.queries';
import { useCreateEventTicket } from '../../CreateEventV2/hooks/useCreateEventTicket';
import BundleTicketsSection from './BundleTicketsSection';

// Query to get event details for context (includes tickets for bundle support)
const GET_EVENT_BASIC = gql`
  query GetEventBasic($input: GetEventInput!) {
    getEvent(input: $input) {
      id
      name
      account_id
      start_date
      end_date
      eventDates
      reserved_seats
      sales_windows
      admin_tickets {
        id
        name
        price
        start_date
        end_date
      }
      admin_multiday_tickets {
        id
        name
        price
        start_date
        end_date
      }
      admin_other_tickets {
        id
        name
        price
        start_date
        end_date
      }
    }
  }
`;

// Query to get a specific template
const GET_TICKET_TEMPLATE = gql`
  query GetTicketTemplate($id: Int!) {
    getTicketTemplate(id: $id) {
      id
      name
      type
      price
      track_price
      description
      description_image_id
      description_video
      fee_dollar
      fee_percent
      pos_fee_dollar
      pos_fee_percent
      limit
      visibility
      reserved_seat
      reserved_seat_category
      reserved_seat_prefix
      reserved_seat_section
      category_id
      chart_id
      set_member_number
      prefix_text
      suffix_text
      last_member_number
      windows {
        id
        name
        ends
        time
        price
        tiers {
          id
          name
          price
          track_price
          color_code
        }
      }
      tiers {
        id
        name
        price
        track_price
      }
      pages {
        id
        name
        pdf_url
        video_url
        pdf_id
        form_id
        order
        waivers {
          id
          waiver_id
        }
      }
    }
  }
`;

// GraphQL mutation for saving a ticket as a template
const ADD_TICKET_TEMPLATE = gql`
  mutation AddTicketTemplate($input: CreateTicketTemplateInput!) {
    addTicketTemplate(input: $input) {
      id
      name
      type
      price
      track_price
      description
      description_video
      description_image_id
      fee_dollar
      fee_percent
      pos_fee_dollar
      pos_fee_percent
      limit
      visibility
      reserved_seat
      reserved_seat_category
      reserved_seat_prefix
      reserved_seat_section
      category_id
      chart_id
      set_member_number
      prefix_text
      suffix_text
      last_member_number
      windows {
        id
        name
        ends
        time
        price
        tiers {
          id
          name
          price
          track_price
        }
      }
      tiers {
        id
        name
        price
        track_price
      }
      pages {
        id
        name
        pdf_url
        video_url
        pdf_id
        form_id
        order
        waivers {
          id
          waiver_id
        }
      }
    }
  }
`;

// GraphQL query for searching Formstack forms
const SEARCH_FORMSTACK_FORMS = gql`
  query SearchFormstackForms($queryString: String!) {
    searchFormstackForms(queryString: $queryString) {
      form_id
      name
    }
  }
`;

// Query to fetch seating charts
const GET_SEATING_CHARTS = gql`
  query GetSeatingCharts {
    getSeatingCharts {
      id
      name
    }
  }
`;

// Query to get recommended ticket color
const RECOMMEND_TICKET_COLOR = gql`
  query RecommendTicketColor($input: RecommendTicketColorInput!) {
    recommendTicketColor(input: $input)
  }
`;

// Page styling
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme?.colors?.background?.primary ||
    (theme?.name === 'dark' ? '#1a1a2e' : '#f8fafc')};
  font-family: ${({ theme }) =>
    theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'};
`;

const Container = styled.div`
  padding: 30px;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div``;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

const BackButtonWrapper = styled.div`
  margin-bottom: 16px;

  button {
    width: auto;
    padding: 0;
    background: none;
    border: none;
    box-shadow: none;

    &:hover {
      background: none;
      box-shadow: none;
    }
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0;
  line-height: 1.6;
`;

const HeaderButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 10px 24px;
  }
`;

// Step Navigation
const StepperContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
  padding: 0 16px;

  @media (max-width: 480px) {
    padding: 0 12px;
  }
`;

const StepperTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
`;

const StepCircle = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  transition: all 0.2s;
  color: white;

  background: ${({ status, theme }) =>
    status === 'completed' || status === 'active'
      ? theme.colors.primary
      : theme.name === 'dark'
      ? '#374151'
      : '#E5E7EB'};

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
`;

const StepLabel = styled.span`
  margin-left: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ status, theme }) =>
    status === 'active' || status === 'completed'
      ? theme.name === 'dark'
        ? '#ffffff'
        : theme.colors.blueBlack
      : theme.name === 'dark'
      ? '#64748B'
      : '#9CA3AF'};

  @media (max-width: 640px) {
    display: none;
  }
`;

const StepConnector = styled.div`
  width: 100px;
  height: 2px;
  background: ${({ completed, theme }) =>
    completed
      ? theme.colors.primary
      : theme.name === 'dark'
      ? '#374151'
      : '#E5E7EB'};
  margin: 0 12px;

  @media (max-width: 768px) {
    width: 80px;
  }

  @media (max-width: 480px) {
    width: 40px;
    margin: 0 6px;
  }

  @media (max-width: 380px) {
    width: 30px;
    margin: 0 4px;
  }
`;

const StepNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.card.border};
`;

const NavButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 10px 20px;
  }
`;

// Two column grid
const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Form Card
const FormCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: ${({ noMargin }) => (noMargin ? '0' : '24px')};
`;

const FormSection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionDescription = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: -8px 0 16px 0;
  line-height: 1.5;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || '1fr'};
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.dark};
  margin-bottom: 8px;
`;

const LabelWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.dark};
  margin-bottom: 8px;
`;

const RequiredBadge = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.status.error};
  margin-left: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme.colors.input?.background || theme.colors.card.background};
  border: 1px solid
    ${({ theme, error }) =>
      error ? theme.colors.status.error : theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryOpacity(0.1)};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textGray.muted};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme.colors.input?.background || theme.colors.card.background};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s;
  min-height: 80px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryOpacity(0.1)};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textGray.muted};
  }
`;

const ErrorText = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.error};
  margin-top: 6px;
`;

// Toggle Components
const ToggleCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${({ theme }) => theme.colors.background.overlay};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const ToggleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToggleTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ToggleDescription = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.card.border};

  &:last-child {
    border-bottom: none;
  }
`;

// Sales Windows
const SalesWindowsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SalesWindowCard = styled.div`
  background: ${({ theme }) => theme.colors.background.overlay};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 16px;
  position: relative;
`;

const SalesWindowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SalesWindowTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textGray.dark};
`;

const SmallIconButtonWrapper = styled.div`
  button {
    width: 32px;
    height: 32px;
    padding: 0;
  }
`;

const AddWindowButtonWrapper = styled.div`
  button {
    width: 100%;
    padding: 12px 20px;
    border-style: dashed;
  }
`;

const EmptyWindowsMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  text-align: center;
  padding: 24px;
  margin: 0;
`;

// Tiered Pricing
const TierSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.card.border};
`;

const TierHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const TierTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AddTierButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 6px 12px;
    font-size: 13px;
  }
`;

const TierRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr auto;
  gap: 12px;
  align-items: end;
  margin-bottom: 8px;
`;

const EmptyTiersMessage = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  text-align: center;
  padding: 20px;
  margin: 0;
  background: ${({ theme }) => theme.colors.background.overlay};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px dashed ${({ theme }) => theme.colors.card.border};
`;

const RemoveTierButtonWrapper = styled.div`
  button {
    width: 36px;
    height: 36px;
    padding: 0;
  }
`;

// Registration Pages
const PagesContainer = styled.div`
  display: flex;
  gap: 40px;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const PagesListSection = styled.div`
  flex: 2;
`;

const PagesOrderSection = styled.div`
  flex: 1;
  min-width: 250px;

  @media (max-width: 900px) {
    order: -1;
  }
`;

const PageCard = styled.div`
  background: ${({ theme }) => theme.colors.background.overlay};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 20px;
  margin-bottom: 16px;
  position: relative;
`;

const PageCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const PageCardTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textGray.dark};
`;

const DeletePageButtonWrapper = styled.div`
  button {
    width: 36px;
    height: 36px;
    padding: 0;
  }
`;

const AddPageButtonWrapper = styled.div`
  button {
    width: 100%;
    padding: 14px 24px;
  }
`;

const EmptyPagesMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  text-align: center;
  padding: 40px 24px;
  margin: 0 0 16px 0;
  background: ${({ theme }) => theme.colors.background.overlay};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px dashed ${({ theme }) => theme.colors.card.border};
`;

const OrderSectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DraggablePageItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.card.background};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: 8px;
  cursor: move;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const DragHandle = styled.div`
  color: ${({ theme }) => theme.colors.textGray.muted};
  display: flex;
  align-items: center;
`;

const PageOrderName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.dark};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WaiversContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.muted};
`;

const WaiverRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > div:first-child {
    flex: 1;
  }
`;

const RemoveWaiverButtonWrapper = styled.div`
  button {
    width: 28px;
    height: 28px;
    padding: 0;
  }
`;

const AddWaiverButtonWrapper = styled.div`
  margin-top: 8px;

  button {
    width: auto;
    padding: 8px 16px;
    font-size: 13px;
  }
`;

// Icons
const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const DragHandleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const MinusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" stroke="currentColor" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

// Platform detection
const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';

const TICKET_TYPES = [
  { value: 'ticket', label: 'Ticket' },
  { value: 'other', label: 'Other' },
  ...(isTicketHoss ? [{ value: 'bundle', label: 'Bundle' }] : []),
  ...(!isTicketHoss ? [{ value: 'registration', label: 'Registration' }] : []),
];

const VISIBILITY_OPTIONS = [
  { value: 'visible', label: 'Visible' },
  { value: 'hidden', label: 'Hidden' },
];

// Default fee calculation
const getDefaultTicketFees = (price, ticketType, reservedSeat = false) => {
  let fee_dollar, fee_percent;
  const numPrice = parseFloat(price) || 0;

  if (isTicketHoss) {
    // "Other" tickets have different fee structure ($0 + 5.25%)
    if (ticketType === 'other') {
      fee_dollar = 0;
      fee_percent = 5.25;
    } else if (numPrice >= 1 && numPrice <= 5) {
      fee_dollar = reservedSeat ? 1.25 : 0.75;
      fee_percent = 3.25;
    } else if (numPrice > 5) {
      fee_dollar = reservedSeat ? 1.75 : 1.25;
      fee_percent = 3.25;
    }
  } else {
    if (ticketType === 'other' || ticketType === 'registration') {
      if (numPrice >= 1 && numPrice <= 500) {
        fee_dollar = 0;
        fee_percent = 5.25;
      } else if (numPrice > 500) {
        fee_dollar = 10;
        fee_percent = 3.25;
      }
    } else {
      if (numPrice >= 1 && numPrice <= 1000000) {
        fee_dollar = 1.85;
        fee_percent = 3.25;
      }
    }
  }

  return { fee_dollar, fee_percent };
};

const getDefaultPosFees = (price, reservedSeat = false) => {
  let pos_fee_dollar, pos_fee_percent;
  const numPrice = parseFloat(price) || 0;

  if (isTicketHoss) {
    if (reservedSeat) {
      pos_fee_dollar = 1.25;
      pos_fee_percent = 3.25;
    } else {
      pos_fee_dollar = 0;
      pos_fee_percent = 5.25;
    }
  } else {
    if (numPrice >= 1 && numPrice <= 1000000) {
      pos_fee_dollar = 0;
      pos_fee_percent = 5.25;
    }
  }

  return { pos_fee_dollar, pos_fee_percent };
};

const AddEventTicketContent = () => {
  const { eventId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const inputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
  const infoIconColor =
    theme?.colors?.textGray?.dark ||
    (theme?.name === 'dark' ? '#e2e8f0' : '#64748b');

  // Get templateId from query params or location state
  const searchParams = new URLSearchParams(location.search);
  const templateId = searchParams.get('templateId');
  const templateFromState = location.state?.template;

  // Context-aware routing
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Fetch event data
  const {
    data: eventData,
    loading: eventLoading,
    error: eventError,
  } = useQuery(GET_EVENT_BASIC, {
    variables: { input: { id: parseInt(eventId) } },
    skip: !eventId,
  });
  const event = eventData?.getEvent;

  // Fetch template if templateId provided
  const { data: templateData } = useQuery(GET_TICKET_TEMPLATE, {
    variables: { id: parseInt(templateId) },
    skip: !templateId || templateFromState,
  });
  const template = templateFromState || templateData?.getTicketTemplate;

  // Fetch waivers for pages
  const { data: waiversData } = useQuery(GET_WAIVERS);
  const waivers = waiversData?.getWaivers || [];

  // Fetch seating charts for reserved seating
  const { data: chartsData, loading: chartsLoading } =
    useQuery(GET_SEATING_CHARTS);
  const seatingChartOptions = (chartsData?.getSeatingCharts || []).map(
    (chart) => ({
      value: chart.id,
      label: chart.name,
    })
  );

  // Fetch recommended ticket color
  const { data: colorData } = useQuery(RECOMMEND_TICKET_COLOR, {
    variables: {
      input: {
        event_id: parseInt(eventId),
        account_id: event?.account_id ? parseInt(event.account_id) : 0,
        date: event?.start_date || '',
      },
    },
    skip: !eventId || !event?.account_id || !event?.start_date,
    fetchPolicy: 'network-only',
  });
  const recommendedColor = colorData?.recommendTicketColor;

  // Mutation for creating event tickets
  const { createEventTicket } = useCreateEventTicket();

  // Check if we came from the wizard (via location state)
  const cameFromWizard = location.state?.fromWizard === true;
  const ticketTypeFromState = location.state?.ticketType;

  // Fetch Formstack forms for pages
  const [formstackSearchQuery, setFormstackSearchQuery] = useState('');
  const {
    data: formstackData,
    loading: formstackLoading,
    refetch: refetchFormstack,
  } = useQuery(SEARCH_FORMSTACK_FORMS, {
    variables: { queryString: formstackSearchQuery },
    skip: formstackSearchQuery.length < 4,
  });
  const formstackOptions = (formstackData?.searchFormstackForms || []).map(
    (form) => ({
      label: form.name,
      value: form.form_id,
    })
  );

  const handleFormstackSearch = (query) => {
    setFormstackSearchQuery(query);
    if (query.length >= 4) {
      refetchFormstack({ queryString: query });
    }
  };

  // Form state - includes all template fields plus event-specific fields
  const [formData, setFormData] = useState({
    // Basic info
    name: '',
    type: 'ticket',
    description: '',
    description_image_id: null,
    description_image_url: null,
    description_video: '',
    // Event-specific fields
    start_date: '',
    end_date: '',
    sale_start: '',
    sale_end: '',
    on_sale_time: '',
    off_sale_time: '',
    color_code: '',
    // Pricing
    price: '',
    track_price: '',
    fee_dollar: '',
    fee_percent: '',
    pos_fee_dollar: '',
    pos_fee_percent: '',
    // Options
    visibility: 'visible',
    limit: '',
    is_featured: false,
    featured_text: '',
    category_id: '',
    // Reserved seat options
    reserved_seat: false,
    reserved_seat_category: '',
    reserved_seat_prefix: '',
    reserved_seat_section: false,
    seating_chart_id: null,
    // Member number settings (registration only)
    set_member_number: false,
    prefix_text: '',
    suffix_text: '',
    last_member_number: '',
    // Sales windows toggle
    has_sales_windows: false,
    // Ticket-level tiers
    tiers: [],
    // Sales windows
    windows: [],
    // Registration pages
    pages: [],
    pages_with_order: [],
    // Bundle-specific
    associated_tickets: [],
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [toaster, setToaster] = useState({ isOpen: false });
  const [saving, setSaving] = useState(false);
  const [addTicketTemplate] = useMutation(ADD_TICKET_TEMPLATE);

  // Check ticket type for conditional rendering
  const isRegistration = formData.type === 'registration';
  const isBundle = formData.type === 'bundle';

  // Get available tickets for bundle (combine all non-bundle ticket arrays)
  const availableTicketsForBundle = useMemo(() => {
    if (!event) return [];
    return [
      ...(event.admin_tickets || []),
      ...(event.admin_multiday_tickets || []),
      ...(event.admin_other_tickets || []),
      ...(event.admin_registration_tickets || []),
    ];
  }, [event]);

  // Step configuration - bundles have 4 steps (includes Select Tickets step)
  const steps = isRegistration
    ? [
        { number: 1, label: 'Basic Info' },
        { number: 2, label: 'Pricing' },
        { number: 3, label: 'Options' },
        { number: 4, label: 'Pages' },
      ]
    : isBundle
    ? [
        { number: 1, label: 'Bundle Info' },
        { number: 2, label: 'Pricing' },
        { number: 3, label: 'Select Tickets' },
        { number: 4, label: 'Options' },
      ]
    : [
        { number: 1, label: 'Basic Info' },
        { number: 2, label: 'Pricing' },
        { number: 3, label: 'Options' },
      ];

  const totalSteps = steps.length;

  // Pre-fill from template when available
  useEffect(() => {
    if (template) {
      setFormData((prev) => ({
        ...prev,
        name: template.name || '',
        type: template.type || 'ticket',
        description: template.description || '',
        description_image_id: template.description_image_id || null,
        description_video: template.description_video || '',
        price: template.price?.toString() || '',
        track_price: template.track_price?.toString() || '',
        fee_dollar: template.fee_dollar?.toString() || '',
        fee_percent: template.fee_percent?.toString() || '',
        pos_fee_dollar: template.pos_fee_dollar?.toString() || '',
        pos_fee_percent: template.pos_fee_percent?.toString() || '',
        visibility: template.visibility || 'visible',
        limit: template.limit?.toString() || '',
        category_id: template.category_id?.toString() || '',
        reserved_seat: template.reserved_seat || false,
        reserved_seat_category: template.reserved_seat_category || '',
        reserved_seat_prefix: template.reserved_seat_prefix || '',
        reserved_seat_section: template.reserved_seat_section || false,
        seating_chart_id: template.chart_id || null,
        set_member_number: template.set_member_number || false,
        prefix_text: template.prefix_text || '',
        suffix_text: template.suffix_text || '',
        last_member_number: template.last_member_number?.toString() || '',
        has_sales_windows: (template.windows?.length || 0) > 0,
        tiers:
          template.tiers?.map((t) => ({
            id: t.id || `tier_${Date.now()}_${Math.random()}`,
            name: t.name || '',
            price: t.price?.toString() || '',
            track_price: t.track_price?.toString() || '',
            color_code: t.color_code || '',
          })) || [],
        windows:
          template.windows?.map((w) => ({
            id: w.id || `window_${Date.now()}_${Math.random()}`,
            name: w.name || '',
            ends: w.ends || '1 week before event',
            time: w.time || '23:59',
            price: w.price?.toString() || '',
            tiers:
              w.tiers?.map((t) => ({
                id: t.id || `tier_${Date.now()}_${Math.random()}`,
                name: t.name || '',
                price: t.price?.toString() || '',
                track_price: t.track_price?.toString() || '',
                color_code: t.color_code || '',
              })) || [],
          })) || [],
        pages:
          template.pages?.map((p, index) => ({
            id: p.id || `page_${Date.now()}_${Math.random()}`,
            name: p.name || '',
            pdf_url: p.pdf_url || '',
            video_url: p.video_url || '',
            pdf_id: p.pdf_id || null,
            form_id: p.form_id || '',
            waivers: (p.waivers || [])
              .filter(Boolean)
              .map((w) => ({ waiver: w.waiver_id || w.id })),
            order: p.order ?? index + 1,
          })) || [],
        pages_with_order:
          template.pages?.map((p, index) => ({
            id: p.id || `page_${Date.now()}_${Math.random()}`,
            name: p.name || '',
            pdf_url: p.pdf_url || '',
            video_url: p.video_url || '',
            pdf_id: p.pdf_id || null,
            form_id: p.form_id || '',
            waivers: (p.waivers || [])
              .filter(Boolean)
              .map((w) => ({ waiver: w.waiver_id || w.id })),
            order: p.order ?? index + 1,
          })) || [],
      }));
    }
  }, [template]);

  // Set ticket type from navigation state (when coming from CreateEventTickets)
  useEffect(() => {
    if (ticketTypeFromState && !template) {
      // Map the ticketType from CreateEventTickets to formData.type
      // single/multi -> 'ticket', bundle -> 'bundle', registration -> 'registration', other -> 'other'
      let formType = 'ticket';
      if (ticketTypeFromState === 'bundle') {
        formType = 'bundle';
      } else if (ticketTypeFromState === 'registration') {
        formType = 'registration';
      } else if (ticketTypeFromState === 'other') {
        formType = 'other';
      }
      // single and multi both use type 'ticket'

      setFormData((prev) => ({
        ...prev,
        type: formType,
      }));
    }
  }, [ticketTypeFromState, template]);

  // Set recommended color when it loads (only if color not already set and not from template)
  useEffect(() => {
    if (recommendedColor && !formData.color_code && !template) {
      setFormData((prev) => ({
        ...prev,
        color_code: recommendedColor,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedColor, template]);

  // Auto-populate date for single day events (for single, registration, and other ticket types)
  useEffect(() => {
    if (!event || template) return;

    const isSingleDayEvent = event.start_date === event.end_date;
    // Pre-fill dates for single-day events, except bundles (no dates) and multi-day (spans multiple days)
    const shouldPrefillDates =
      ticketTypeFromState === 'single' ||
      ticketTypeFromState === 'registration' ||
      ticketTypeFromState === 'other';

    if (isSingleDayEvent && shouldPrefillDates && !formData.start_date) {
      setFormData((prev) => ({
        ...prev,
        start_date: event.start_date,
        end_date: event.start_date,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, ticketTypeFromState, template]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Apply default fees when price, type, or reserved_seat changes (only if not from template)
  useEffect(() => {
    if (template) return; // Don't override template fees

    const numPrice = parseFloat(formData.price) || 0;
    if (numPrice < 1) return;

    const { fee_dollar, fee_percent } = getDefaultTicketFees(
      formData.price,
      formData.type,
      formData.reserved_seat
    );
    const { pos_fee_dollar, pos_fee_percent } = getDefaultPosFees(
      formData.price,
      formData.reserved_seat
    );

    setFormData((prev) => ({
      ...prev,
      fee_dollar: fee_dollar !== undefined ? fee_dollar : prev.fee_dollar,
      fee_percent: fee_percent !== undefined ? fee_percent : prev.fee_percent,
      pos_fee_dollar:
        pos_fee_dollar !== undefined ? pos_fee_dollar : prev.pos_fee_dollar,
      pos_fee_percent:
        pos_fee_percent !== undefined ? pos_fee_percent : prev.pos_fee_percent,
    }));
  }, [formData.price, formData.type, formData.reserved_seat, template]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // Attachment handlers
  const handleAttachmentsChange = (attachments) => {
    if (attachments.length > 0) {
      setFormData((prev) => ({
        ...prev,
        description_image_id: parseInt(attachments[0].id) || null,
        description_image_url: attachments[0].url,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        description_image_id: null,
        description_image_url: null,
      }));
    }
  };

  const getAttachmentsFromImage = () => {
    if (!formData.description_image_url) return [];
    return [
      {
        id: formData.description_image_id || 'description_image',
        name: 'Description Image',
        size: 0,
        sizeFormatted: '',
        url: formData.description_image_url,
        type: 'image/*',
        isImage: true,
      },
    ];
  };

  // Tier handlers
  const addTier = () => {
    const newTier = {
      id: `tier_${Date.now()}`,
      name: '',
      price: '',
      track_price: '',
      color_code: '',
    };
    setFormData((prev) => ({
      ...prev,
      tiers: [...prev.tiers, newTier],
    }));
  };

  const removeTier = (index) => {
    setFormData((prev) => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index),
    }));
  };

  const updateTier = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      tiers: prev.tiers.map((tier, i) =>
        i === index ? { ...tier, [field]: value } : tier
      ),
    }));
  };

  // Sales Windows handlers
  const addSalesWindow = () => {
    const newWindow = {
      id: `window_${Date.now()}`,
      name: '',
      ends: '',
      time: '23:59',
      price: '',
      tiers: [],
    };
    setFormData((prev) => ({
      ...prev,
      windows: [...prev.windows, newWindow],
    }));
  };

  const removeSalesWindow = (index) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.filter((_, i) => i !== index),
    }));
  };

  const updateSalesWindow = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((window, i) =>
        i === index ? { ...window, [field]: value } : window
      ),
    }));
  };

  // Tier handlers for sales windows
  const addTierToWindow = (windowIndex) => {
    const newTier = {
      id: `tier_${Date.now()}`,
      name: '',
      price: '',
      track_price: '',
      color_code: '',
    };
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((window, i) =>
        i === windowIndex
          ? { ...window, tiers: [...window.tiers, newTier] }
          : window
      ),
    }));
  };

  const removeTierFromWindow = (windowIndex, tierIndex) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((window, i) =>
        i === windowIndex
          ? {
              ...window,
              tiers: window.tiers.filter((_, ti) => ti !== tierIndex),
            }
          : window
      ),
    }));
  };

  const updateTierInWindow = (windowIndex, tierIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((window, i) =>
        i === windowIndex
          ? {
              ...window,
              tiers: window.tiers.map((tier, ti) =>
                ti === tierIndex ? { ...tier, [field]: value } : tier
              ),
            }
          : window
      ),
    }));
  };

  // Registration Pages handlers
  const addPage = () => {
    const newOrder =
      formData.pages.length > 0
        ? getHighestItemOrderNumber(formData.pages) + 1
        : 1;
    const newPage = {
      id: `page_${Date.now()}`,
      name: '',
      pdf_url: '',
      video_url: '',
      pdf_id: null,
      form_id: null,
      waivers: [],
      order: newOrder,
    };
    setFormData((prev) => ({
      ...prev,
      pages: [...prev.pages, newPage],
      pages_with_order: [...prev.pages_with_order, newPage],
    }));
  };

  const removePage = (index) => {
    const pageToRemove = formData.pages[index];
    setFormData((prev) => ({
      ...prev,
      pages: prev.pages.filter((_, i) => i !== index),
      pages_with_order: prev.pages_with_order.filter((p) =>
        pageToRemove.id
          ? p.id !== pageToRemove.id
          : p.name !== pageToRemove.name
      ),
    }));
  };

  const updatePage = (index, field, value) => {
    setFormData((prev) => {
      const updatedPages = prev.pages.map((page, i) =>
        i === index ? { ...page, [field]: value } : page
      );
      const pageToUpdate = prev.pages[index];
      const updatedPagesWithOrder = prev.pages_with_order.map((p) =>
        (pageToUpdate.id && p.id === pageToUpdate.id) ||
        (!pageToUpdate.id && p.order === pageToUpdate.order)
          ? { ...p, [field]: value }
          : p
      );
      return {
        ...prev,
        pages: updatedPages,
        pages_with_order: updatedPagesWithOrder,
      };
    });
  };

  const addWaiverToPage = (pageIndex) => {
    setFormData((prev) => {
      const pageToUpdate = prev.pages[pageIndex];
      const updatedPages = prev.pages.map((page, i) =>
        i === pageIndex
          ? { ...page, waivers: [...(page.waivers || []), { waiver: '' }] }
          : page
      );
      const updatedPagesWithOrder = prev.pages_with_order.map((p) =>
        (pageToUpdate.id && p.id === pageToUpdate.id) ||
        (!pageToUpdate.id && p.order === pageToUpdate.order)
          ? { ...p, waivers: [...(p.waivers || []), { waiver: '' }] }
          : p
      );
      return {
        ...prev,
        pages: updatedPages,
        pages_with_order: updatedPagesWithOrder,
      };
    });
  };

  const removeWaiverFromPage = (pageIndex, waiverIndex) => {
    setFormData((prev) => {
      const pageToUpdate = prev.pages[pageIndex];
      const updatedPages = prev.pages.map((page, i) =>
        i === pageIndex
          ? {
              ...page,
              waivers: page.waivers.filter((_, wi) => wi !== waiverIndex),
            }
          : page
      );
      const updatedPagesWithOrder = prev.pages_with_order.map((p) =>
        (pageToUpdate.id && p.id === pageToUpdate.id) ||
        (!pageToUpdate.id && p.order === pageToUpdate.order)
          ? {
              ...p,
              waivers: p.waivers.filter((_, wi) => wi !== waiverIndex),
            }
          : p
      );
      return {
        ...prev,
        pages: updatedPages,
        pages_with_order: updatedPagesWithOrder,
      };
    });
  };

  const updateWaiverInPage = (pageIndex, waiverIndex, value) => {
    setFormData((prev) => {
      const pageToUpdate = prev.pages[pageIndex];
      const updatedPages = prev.pages.map((page, i) =>
        i === pageIndex
          ? {
              ...page,
              waivers: page.waivers.map((w, wi) =>
                wi === waiverIndex ? { waiver: value } : w
              ),
            }
          : page
      );
      const updatedPagesWithOrder = prev.pages_with_order.map((p) =>
        (pageToUpdate.id && p.id === pageToUpdate.id) ||
        (!pageToUpdate.id && p.order === pageToUpdate.order)
          ? {
              ...p,
              waivers: p.waivers.map((w, wi) =>
                wi === waiverIndex ? { waiver: value } : w
              ),
            }
          : p
      );
      return {
        ...prev,
        pages: updatedPages,
        pages_with_order: updatedPagesWithOrder,
      };
    });
  };

  const handlePagesSort = (sortedPages) => {
    const reorderedPages = sortedPages.map((page, index) => ({
      ...page,
      order: index + 1,
    }));
    setFormData((prev) => ({
      ...prev,
      pages_with_order: reorderedPages,
      pages: prev.pages.map((page) => {
        const sortedPage = reorderedPages.find((p) => p.id === page.id);
        return sortedPage ? { ...page, order: sortedPage.order } : page;
      }),
    }));
  };

  // Step navigation
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.name.trim()) {
        newErrors.name = 'Ticket name is required';
      }
      // Bundles don't require dates
      if (!isBundle && !formData.start_date) {
        newErrors.start_date = 'Start date is required';
      }
    }

    if (step === 2) {
      if (!formData.price && formData.price !== 0) {
        newErrors.price = 'Price is required';
      } else if (
        isNaN(parseFloat(formData.price)) ||
        parseFloat(formData.price) < 0
      ) {
        newErrors.price = 'Price must be a valid positive number';
      }
    }

    // Step 3 validation for bundles: at least one ticket must be selected
    if (step === 3 && isBundle) {
      const hasSelectedTickets = formData.associated_tickets.some(
        (t) => t.quantity > 0
      );
      if (!hasSelectedTickets) {
        newErrors.associated_tickets =
          'Please select at least one ticket with quantity greater than 0';
      }
    }

    // Step 3 validation for non-bundles: category is required
    if (step === 3 && !isBundle) {
      if (!formData.category_id) {
        newErrors.category_id = 'Category is required';
      }
    }

    // Step 4 validation for bundles: category is required
    if (step === 4 && isBundle) {
      if (!formData.category_id) {
        newErrors.category_id = 'Category is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const goToStep = (step) => {
    if (step < currentStep) {
      setCurrentStep(step);
    } else if (step === currentStep + 1 && validateStep(currentStep)) {
      setCurrentStep(step);
    }
  };

  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'active';
    return 'pending';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Ticket name is required';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    // Bundles don't require dates
    if (!isBundle && !formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.price && formData.price !== 0) {
      newErrors.price = 'Price is required';
    } else if (
      isNaN(parseFloat(formData.price)) ||
      parseFloat(formData.price) < 0
    ) {
      newErrors.price = 'Price must be a valid positive number';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Determine which step has the first error and navigate there
      const firstError = Object.keys(newErrors)[0];
      const step1Fields = ['name', 'start_date'];
      const step2Fields = ['price'];
      // category_id is on step 3 for non-bundles, step 4 for bundles
      if (step1Fields.includes(firstError)) {
        setCurrentStep(1);
      } else if (step2Fields.includes(firstError)) {
        setCurrentStep(2);
      } else if (firstError === 'category_id') {
        setCurrentStep(isBundle ? 4 : 3);
      }

      // Show toaster with first error message
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Validation Error',
        message: Object.values(newErrors)[0],
      });
      return false;
    }

    return true;
  };

  const handleBack = () => {
    if (cameFromWizard) {
      // Go back to the ticket wizard
      history.push(`${routePrefix}/events/addEvent/${eventId}/tickets`);
    } else {
      // Go back to the edit event page
      history.push(`${routePrefix}/events/editEvent/${eventId}?tab=tickets`);
    }
  };

  // Convert YYYY-MM-DD to MM-DD-YYYY format for API
  const convertDateFormat = (dateStr) => {
    if (!dateStr) return null;
    // Check if already in MM-DD-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return dateStr;
    // Convert from YYYY-MM-DD to MM-DD-YYYY
    const parts = dateStr.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[1]}-${parts[2]}-${parts[0]}`;
    }
    return dateStr;
  };

  const handleSaveAsTemplate = async (savedFormData) => {
    if (!event?.account_id) return;

    try {
      const templateInput = {
        account_id: parseInt(event.account_id),
        name: savedFormData.name.trim(),
        type: savedFormData.type,
        price: parseFloat(savedFormData.price) || 0,
        track_price: parseFloat(savedFormData.track_price) || 0,
        description: savedFormData.description || null,
        description_image_id: savedFormData.description_image_id
          ? parseInt(savedFormData.description_image_id)
          : null,
        description_video: savedFormData.description_video || null,
        fee_dollar: parseFloat(savedFormData.fee_dollar) || 0,
        fee_percent: parseFloat(savedFormData.fee_percent) || 0,
        pos_fee_dollar: parseFloat(savedFormData.pos_fee_dollar) || 0,
        pos_fee_percent: parseFloat(savedFormData.pos_fee_percent) || 0,
        limit: savedFormData.limit ? parseInt(savedFormData.limit) : null,
        visibility: savedFormData.visibility || 'visible',
        reserved_seat: savedFormData.reserved_seat || false,
        reserved_seat_category: savedFormData.reserved_seat_category || null,
        reserved_seat_prefix: savedFormData.reserved_seat_prefix || null,
        reserved_seat_section: savedFormData.reserved_seat_section || false,
        category_id: savedFormData.category_id
          ? Number(savedFormData.category_id)
          : null,
        chart_id: savedFormData.seating_chart_id || null,
        set_member_number: savedFormData.set_member_number || false,
        prefix_text: savedFormData.prefix_text || null,
        suffix_text: savedFormData.suffix_text || null,
        last_member_number: savedFormData.last_member_number
          ? parseInt(savedFormData.last_member_number)
          : null,
        windows:
          savedFormData.windows?.length > 0
            ? savedFormData.windows.map((w) => ({
                name: w.name,
                ends: w.ends,
                time: w.time,
                price: parseFloat(w.price) || 0,
                tiers: w.tiers?.map((t) => ({
                  name: t.name,
                  price: parseFloat(t.price) || 0,
                  track_price: parseFloat(t.track_price) || 0,
                })),
              }))
            : null,
        tiers:
          savedFormData.tiers?.length > 0
            ? savedFormData.tiers.map((t) => ({
                name: t.name,
                price: parseFloat(t.price) || 0,
                track_price: parseFloat(t.track_price) || 0,
              }))
            : null,
        pages:
          savedFormData.pages_with_order?.length > 0
            ? savedFormData.pages_with_order.map((p) => ({
                name: p.name,
                pdf_url: p.pdf_url || null,
                video_url: p.video_url || null,
                pdf_id: p.pdf_id ? Number(p.pdf_id) : null,
                form_id: p.form_id ? String(p.form_id) : null,
                waivers: (p.waivers || [])
                  .map((w) => w.waiver)
                  .filter(Boolean)
                  .map((w) => ({ waiver_id: Number(w) })),
                order: p.order,
              }))
            : null,
      };

      await addTicketTemplate({ variables: { input: templateInput } });

      setToaster({
        isOpen: true,
        type: 'success',
        title: 'Template Saved',
        message: `"${savedFormData.name.trim()}" has been saved as a template.`,
      });

      setTimeout(() => {
        handleBack();
      }, 1500);
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to save template. Please try again.',
      });
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);

    try {
      // Build the mutation input from form data
      const input = {
        event_id: Number(eventId),
        name: formData.name.trim(),
        price: parseFloat(formData.price) || 0,
        track_price: parseFloat(formData.track_price) || 0,
        type: formData.type,
        start_date: convertDateFormat(formData.start_date),
        end_date: convertDateFormat(formData.end_date),
        sale_start: convertDateFormat(formData.sale_start) || null,
        sale_end: convertDateFormat(formData.sale_end) || null,
        on_sale_time: formData.on_sale_time || null,
        off_sale_time: formData.off_sale_time || null,
        color_code: formData.color_code || null,
        limit: formData.limit ? parseInt(formData.limit) : null,
        description: formData.description || null,
        description_video: formData.description_video || null,
        fee_dollar: parseFloat(formData.fee_dollar) || 0,
        fee_percent: parseFloat(formData.fee_percent) || 0,
        pos_fee_dollar: parseFloat(formData.pos_fee_dollar) || 0,
        pos_fee_percent: parseFloat(formData.pos_fee_percent) || 0,
        category_id: formData.category_id ? Number(formData.category_id) : null,
        visibility: formData.visibility || 'visible',
        set_member_number: formData.set_member_number || false,
        prefix_text: formData.prefix_text || null,
        suffix_text: formData.suffix_text || null,
        last_member_number: formData.last_member_number
          ? parseInt(formData.last_member_number)
          : null,
        // Include windows if present
        windows:
          formData.windows?.length > 0
            ? formData.windows.map((w) => ({
                name: w.name,
                ends: w.ends,
                time: w.time,
                price: parseFloat(w.price) || 0,
                tiers: w.tiers?.map((t) => ({
                  name: t.name,
                  price: parseFloat(t.price) || 0,
                  track_price: parseFloat(t.track_price) || 0,
                  color_code: t.color_code || null,
                })),
              }))
            : null,
        // Include tiers if present
        tiers:
          formData.tiers?.length > 0
            ? formData.tiers.map((t) => ({
                name: t.name,
                price: parseFloat(t.price) || 0,
                track_price: parseFloat(t.track_price) || 0,
                color_code: t.color_code || null,
              }))
            : null,
        // Include pages if present (for registration tickets)
        pages:
          formData.pages_with_order?.length > 0
            ? formData.pages_with_order.map((p) => ({
                name: p.name,
                pdf_url: p.pdf_url || null,
                video_url: p.video_url || null,
                pdf_id: p.pdf_id ? Number(p.pdf_id) : null,
                form_id: p.form_id ? String(p.form_id) : null,
                waivers: (p.waivers || [])
                  .map((w) => w.waiver)
                  .filter(Boolean)
                  .map((w) => ({ waiver_id: Number(w) })),
                order: p.order,
              }))
            : null,
        // Include associated_tickets for bundles
        associated_tickets:
          formData.associated_tickets?.length > 0
            ? formData.associated_tickets.map((t) => ({
                ticket_id: Number(t.id),
                quantity: t.quantity || 1,
              }))
            : null,
        // Description image and featured ticket
        description_image_id: formData.description_image_id
          ? parseInt(formData.description_image_id)
          : null,
        is_featured: formData.is_featured || false,
        featured_text: formData.is_featured
          ? formData.featured_text || null
          : null,
        // Reserved seating fields
        reserved_seat: formData.reserved_seat || false,
        reserved_seat_category: formData.reserved_seat_category || null,
        reserved_seat_prefix: formData.reserved_seat_prefix || null,
        reserved_seat_section: formData.reserved_seat_section || false,
        chart_id: formData.seating_chart_id || null,
      };

      // Call mutation to create the ticket
      const response = await createEventTicket(input);

      if (response?.data?.createEventTicket?.id) {
        const createdFormData = { ...formData };

        if (event?.account_id) {
          setToaster({
            isOpen: true,
            type: 'success',
            title: 'Ticket Created',
            message: `"${formData.name.trim()}" has been added to the event.`,
            action: {
              text: 'Save as Template',
              onClick: () => handleSaveAsTemplate(createdFormData),
            },
            navigateOnClose: true,
          });
        } else {
          setToaster({
            isOpen: true,
            type: 'success',
            title: 'Ticket Created',
            message: `"${formData.name.trim()}" has been added to the event.`,
          });

          // Navigate back after showing success message
          setTimeout(() => {
            handleBack();
          }, 1500);
        }
      }
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to create ticket. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (eventLoading) {
    return (
      <PageWrapper>
        <Container>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            Loading event data...
          </div>
        </Container>
      </PageWrapper>
    );
  }

  // Error state
  if (eventError) {
    return (
      <PageWrapper>
        <Container>
          <BackButtonWrapper>
            <ButtonV2
              type="button"
              onClick={handleBack}
              icon={<ArrowLeftIcon />}
            >
              Back to Event
            </ButtonV2>
          </BackButtonWrapper>
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#ef4444',
            }}
          >
            Failed to load event data. Please try again.
          </div>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <BackButtonWrapper>
          <ButtonV2 type="button" onClick={handleBack} icon={<ArrowLeftIcon />}>
            Back to Event
          </ButtonV2>
        </BackButtonWrapper>

        <PageHeader>
          <HeaderLeft>
            <Title>
              {template ? 'Add Ticket from Template' : 'Add Custom Ticket'}
            </Title>
            <Subtitle>
              {currentStep === 1 &&
                'Set up basic details and event-specific settings'}
              {currentStep === 2 && 'Configure pricing and sales options'}
              {currentStep === 3 &&
                isBundle &&
                'Select which tickets to include in this bundle'}
              {currentStep === 3 &&
                !isBundle &&
                'Set additional options and settings'}
              {currentStep === 4 &&
                isBundle &&
                'Set additional options and settings'}
              {currentStep === 4 &&
                isRegistration &&
                'Configure registration pages for checkout'}
            </Subtitle>
          </HeaderLeft>
          <HeaderRight>
            <HeaderButtonWrapper>
              <ButtonV2 type="button" onClick={handleBack}>
                Cancel
              </ButtonV2>
            </HeaderButtonWrapper>
          </HeaderRight>
        </PageHeader>

        {/* Step Indicator */}
        <StepperContainer>
          <StepperTrack>
            {steps.map((step, index) => (
              <StepItem key={step.number}>
                <StepCircle
                  type="button"
                  status={getStepStatus(step.number)}
                  isClickable={step.number < currentStep}
                  onClick={() => goToStep(step.number)}
                >
                  {step.number}
                </StepCircle>
                <StepLabel status={getStepStatus(step.number)}>
                  {step.label}
                </StepLabel>
                {index < steps.length - 1 && (
                  <StepConnector completed={step.number < currentStep} />
                )}
              </StepItem>
            ))}
          </StepperTrack>
        </StepperContainer>

        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <FormCard>
            <FormSection>
              <SectionTitle>Basic Information</SectionTitle>

              <FormRow columns="2fr 1fr">
                <FormGroup>
                  <Label>
                    Ticket Name
                    <RequiredBadge>*Required</RequiredBadge>
                  </Label>
                  <Input
                    ref={inputRef}
                    type="text"
                    placeholder="e.g., General Admission - Saturday"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    error={!!errors.name}
                  />
                  {errors.name && <ErrorText>{errors.name}</ErrorText>}
                </FormGroup>

                <FormFieldSelect
                  label="Type"
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  options={TICKET_TYPES}
                />
              </FormRow>

              {/* Event-Specific: Date and Color (not for bundles) */}
              {!isBundle && (
                <>
                  {ticketTypeFromState === 'single' ? (
                    /* Single day ticket - one date field */
                    <FormRow columns="1fr 1fr" style={{ marginTop: '16px' }}>
                      <FormGroup>
                        <Label>
                          Ticket Date
                          <RequiredBadge>*Required</RequiredBadge>
                        </Label>
                        <DateTimePicker
                          value={formData.start_date}
                          onChange={(value) => {
                            handleChange('start_date', value);
                            handleChange('end_date', value);
                          }}
                          showTime={false}
                        />
                        {errors.start_date && (
                          <ErrorText>{errors.start_date}</ErrorText>
                        )}
                      </FormGroup>

                      <ColorSelect
                        label="Ticket Color"
                        value={formData.color_code}
                        onChange={(value) => handleChange('color_code', value)}
                        options={COLOR_CODES}
                        placeholder="Select Color"
                        allowEmpty
                        emptyLabel="Select Color"
                      />
                    </FormRow>
                  ) : (
                    /* Multi-day or other ticket types - two date fields */
                    <FormRow
                      columns="1fr 1fr 1fr"
                      style={{ marginTop: '16px' }}
                    >
                      <FormGroup>
                        <Label>
                          Start Date
                          <RequiredBadge>*Required</RequiredBadge>
                        </Label>
                        <DateTimePicker
                          value={formData.start_date}
                          onChange={(value) =>
                            handleChange('start_date', value)
                          }
                          showTime={false}
                        />
                        {errors.start_date && (
                          <ErrorText>{errors.start_date}</ErrorText>
                        )}
                      </FormGroup>

                      <FormGroup>
                        <Label>End Date</Label>
                        <DateTimePicker
                          value={formData.end_date}
                          onChange={(value) => handleChange('end_date', value)}
                          showTime={false}
                        />
                      </FormGroup>

                      <ColorSelect
                        label="Ticket Color"
                        value={formData.color_code}
                        onChange={(value) => handleChange('color_code', value)}
                        options={COLOR_CODES}
                        placeholder="Select Color"
                        allowEmpty
                        emptyLabel="Select Color"
                      />
                    </FormRow>
                  )}
                </>
              )}

              {/* Bundle-specific: Color selection (no date) */}
              {isBundle && (
                <FormRow columns="1fr 2fr" style={{ marginTop: '16px' }}>
                  <ColorSelect
                    label="Bundle Color"
                    value={formData.color_code}
                    onChange={(value) => handleChange('color_code', value)}
                    options={COLOR_CODES}
                    placeholder="Select Color"
                    allowEmpty
                    emptyLabel="Select Color"
                  />
                </FormRow>
              )}

              {/* Sale Dates & Times (not for bundles) */}
              {!isBundle && (
                <FormRow
                  columns="1fr 1fr 1fr 1fr"
                  style={{ marginTop: '16px' }}
                >
                  <FormGroup>
                    <Label>Sale Start Date</Label>
                    <DateTimePicker
                      value={formData.sale_start}
                      onChange={(value) => handleChange('sale_start', value)}
                      showTime={false}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>On Sale Time</Label>
                    <DateTimePicker
                      value={formData.on_sale_time}
                      onChange={(value) => handleChange('on_sale_time', value)}
                      timeOnly
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Sale End Date</Label>
                    <DateTimePicker
                      value={formData.sale_end}
                      onChange={(value) => handleChange('sale_end', value)}
                      showTime={false}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Off Sale Time</Label>
                    <DateTimePicker
                      value={formData.off_sale_time}
                      onChange={(value) => handleChange('off_sale_time', value)}
                      timeOnly
                    />
                  </FormGroup>
                </FormRow>
              )}

              <FormGroup style={{ marginTop: '16px' }}>
                <Label>Description</Label>
                <Textarea
                  placeholder="Enter ticket description..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
              </FormGroup>

              <FormRow columns="1fr 1fr" style={{ marginTop: '16px' }}>
                <FormGroup>
                  <Label>Description Image</Label>
                  <AttachmentUpload
                    attachments={getAttachmentsFromImage()}
                    onAttachmentsChange={handleAttachmentsChange}
                    maxFiles={1}
                    maxFileSize={5242880}
                    channel="SMS"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Description URL (Video)</Label>
                  <Input
                    type="url"
                    placeholder="e.g., https://vimeo.com/... or https://youtube.com/..."
                    value={formData.description_video}
                    onChange={(e) =>
                      handleChange('description_video', e.target.value)
                    }
                  />
                </FormGroup>
              </FormRow>

              {/* Feature Toggles - only show if event has these features enabled */}
              {(event?.reserved_seats || event?.sales_windows) && (
                <TwoColumnGrid style={{ marginTop: '24px' }}>
                  {/* Reserved Seating - only if event has reserved seating enabled */}
                  {event?.reserved_seats && (
                    <ToggleCard>
                      <ToggleInfo>
                        <ToggleTitle>Reserved Seating</ToggleTitle>
                        <ToggleDescription>
                          Enable seat selection for this ticket
                        </ToggleDescription>
                      </ToggleInfo>
                      <Toggle
                        checked={formData.reserved_seat}
                        onChange={(e) =>
                          handleChange('reserved_seat', e.target.checked)
                        }
                      />
                    </ToggleCard>
                  )}

                  {/* Sales Windows - only if event has sales windows enabled */}
                  {event?.sales_windows && (
                    <ToggleCard>
                      <ToggleInfo>
                        <ToggleTitle>Sales Windows</ToggleTitle>
                        <ToggleDescription>
                          Create time-based pricing periods (e.g., Early Bird)
                        </ToggleDescription>
                      </ToggleInfo>
                      <Toggle
                        checked={formData.has_sales_windows}
                        onChange={(e) =>
                          handleChange('has_sales_windows', e.target.checked)
                        }
                      />
                    </ToggleCard>
                  )}
                </TwoColumnGrid>
              )}

              {/* Step Navigation */}
              <StepNavigation>
                <div />
                <NavButtonWrapper>
                  <ButtonV2 elevated type="button" onClick={handleNextStep}>
                    {isMobile ? 'Continue' : 'Continue to Pricing'}
                    <ArrowRightIcon />
                  </ButtonV2>
                </NavButtonWrapper>
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}

        {/* Step 2: Pricing */}
        {currentStep === 2 && (
          <FormCard>
            <FormSection>
              <SectionTitle>Pricing</SectionTitle>

              <FormRow columns="1fr 1fr">
                <FormGroup>
                  <Label>
                    Price
                    <RequiredBadge>*Required</RequiredBadge>
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    error={!!errors.price}
                  />
                  {errors.price && <ErrorText>{errors.price}</ErrorText>}
                </FormGroup>

                <FormGroup>
                  <Label>At Track Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.track_price}
                    onChange={(e) =>
                      handleChange('track_price', e.target.value)
                    }
                  />
                </FormGroup>
              </FormRow>

              {/* Fees */}
              <SectionTitle style={{ marginTop: '24px' }}>Fees</SectionTitle>
              <FormRow columns="1fr 1fr 1fr 1fr">
                <FormGroup>
                  <Label>Fee Dollar Amount (App)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.fee_dollar}
                    onChange={(e) => handleChange('fee_dollar', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <LabelWithTooltip>
                    Fee Percent
                    <Tooltip
                      info
                      content="This percentage needs to include the 3.25% base processing fee."
                      iconColor={infoIconColor}
                      iconSize="16px"
                    />
                  </LabelWithTooltip>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.fee_percent}
                    onChange={(e) =>
                      handleChange('fee_percent', e.target.value)
                    }
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Fee Dollar Amount (POS)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.pos_fee_dollar}
                    onChange={(e) =>
                      handleChange('pos_fee_dollar', e.target.value)
                    }
                  />
                </FormGroup>

                <FormGroup>
                  <LabelWithTooltip>
                    Fee Percent (POS)
                    <Tooltip
                      info
                      content="This percentage needs to include the 3.25% base processing fee."
                      iconColor={infoIconColor}
                      iconSize="16px"
                    />
                  </LabelWithTooltip>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.pos_fee_percent}
                    onChange={(e) =>
                      handleChange('pos_fee_percent', e.target.value)
                    }
                  />
                </FormGroup>
              </FormRow>

              {/* Tiered Pricing - For reserved seats (coexists with sales window tiers) */}
              {formData.reserved_seat && (
                <TierSection>
                  <TierHeader>
                    <TierTitle>Tiered Pricing</TierTitle>
                    <AddTierButtonWrapper>
                      <ButtonV2
                        elevated
                        type="button"
                        onClick={addTier}
                        icon={<PlusIcon />}
                      >
                        Add
                      </ButtonV2>
                    </AddTierButtonWrapper>
                  </TierHeader>

                  {formData.tiers.length === 0 ? (
                    <EmptyTiersMessage>
                      No pricing tiers configured. Add tiers for different seat
                      categories.
                    </EmptyTiersMessage>
                  ) : (
                    <>
                      <TierRow>
                        <Label>Label (Name)</Label>
                        <Label>App Price</Label>
                        <Label>Track Price</Label>
                        <Label>Color</Label>
                        <div />
                      </TierRow>
                      {formData.tiers.map((tier, index) => (
                        <TierRow key={tier.id}>
                          <Input
                            type="text"
                            placeholder="e.g., Front Row"
                            value={tier.name}
                            onChange={(e) =>
                              updateTier(index, 'name', e.target.value)
                            }
                          />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={tier.price}
                            onChange={(e) =>
                              updateTier(index, 'price', e.target.value)
                            }
                          />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={tier.track_price}
                            onChange={(e) =>
                              updateTier(index, 'track_price', e.target.value)
                            }
                          />
                          <ColorSelect
                            value={tier.color_code || ''}
                            onChange={(value) =>
                              updateTier(index, 'color_code', value)
                            }
                            options={COLOR_CODES}
                            placeholder="Select Color"
                            allowEmpty
                            emptyLabel="No Color"
                          />
                          <RemoveTierButtonWrapper>
                            <ButtonV2
                              danger
                              variant="icon"
                              type="button"
                              onClick={() => removeTier(index)}
                              title="Remove tier"
                              icon={<XIcon />}
                            />
                          </RemoveTierButtonWrapper>
                        </TierRow>
                      ))}
                    </>
                  )}
                </TierSection>
              )}

              {/* Sales Windows */}
              {formData.has_sales_windows && (
                <>
                  <SectionTitle style={{ marginTop: '24px' }}>
                    Sales Windows
                  </SectionTitle>
                  <SectionDescription>
                    Create promotional pricing periods. Each window ends at the
                    specified time relative to the event date.
                  </SectionDescription>

                  <SalesWindowsContainer>
                    {formData.windows.length === 0 ? (
                      <EmptyWindowsMessage>
                        No sales windows configured. Add one to enable
                        time-based pricing.
                      </EmptyWindowsMessage>
                    ) : (
                      formData.windows.map((window, windowIndex) => (
                        <SalesWindowCard key={window.id}>
                          <SalesWindowHeader>
                            <SalesWindowTitle>
                              {window.name || `Window ${windowIndex + 1}`}
                            </SalesWindowTitle>
                            <SmallIconButtonWrapper>
                              <ButtonV2
                                danger
                                variant="icon"
                                type="button"
                                onClick={() => removeSalesWindow(windowIndex)}
                                title="Remove window"
                                icon={<XIcon />}
                              />
                            </SmallIconButtonWrapper>
                          </SalesWindowHeader>

                          <FormRow columns="2fr 1fr 130px 120px">
                            <FormGroup>
                              <Label>Window Name</Label>
                              <Input
                                type="text"
                                placeholder="e.g., Early Bird"
                                value={window.name}
                                onChange={(e) =>
                                  updateSalesWindow(
                                    windowIndex,
                                    'name',
                                    e.target.value
                                  )
                                }
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label>End Date</Label>
                              <DateTimePicker
                                value={window.ends}
                                onChange={(value) =>
                                  updateSalesWindow(windowIndex, 'ends', value)
                                }
                                showTime={false}
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label>End Time</Label>
                              <DateTimePicker
                                value={window.time || '23:59'}
                                onChange={(time) =>
                                  updateSalesWindow(windowIndex, 'time', time)
                                }
                                timeOnly
                              />
                            </FormGroup>

                            {!formData.reserved_seat && (
                              <FormGroup>
                                <Label>Price</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={window.price}
                                  onChange={(e) =>
                                    updateSalesWindow(
                                      windowIndex,
                                      'price',
                                      e.target.value
                                    )
                                  }
                                />
                              </FormGroup>
                            )}
                          </FormRow>

                          {/* Tiers inside window - only for reserved seats */}
                          {formData.reserved_seat && (
                            <TierSection style={{ marginTop: '16px' }}>
                              <TierHeader>
                                <TierTitle style={{ fontSize: '13px' }}>
                                  Pricing Tiers
                                </TierTitle>
                                <AddTierButtonWrapper>
                                  <ButtonV2
                                    elevated
                                    type="button"
                                    onClick={() => addTierToWindow(windowIndex)}
                                    icon={<PlusIcon />}
                                  >
                                    Add
                                  </ButtonV2>
                                </AddTierButtonWrapper>
                              </TierHeader>

                              {window.tiers.length === 0 ? (
                                <EmptyTiersMessage>
                                  No tiers. Add tiers for seat-based pricing.
                                </EmptyTiersMessage>
                              ) : (
                                <>
                                  <TierRow>
                                    <Label>Label</Label>
                                    <Label>App Price</Label>
                                    <Label>Track Price</Label>
                                    <Label>Color</Label>
                                    <div />
                                  </TierRow>
                                  {window.tiers.map((tier, tierIndex) => (
                                    <TierRow key={tier.id}>
                                      <Input
                                        type="text"
                                        placeholder="e.g., Front Row"
                                        value={tier.name}
                                        onChange={(e) =>
                                          updateTierInWindow(
                                            windowIndex,
                                            tierIndex,
                                            'name',
                                            e.target.value
                                          )
                                        }
                                      />
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        value={tier.price}
                                        onChange={(e) =>
                                          updateTierInWindow(
                                            windowIndex,
                                            tierIndex,
                                            'price',
                                            e.target.value
                                          )
                                        }
                                      />
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        value={tier.track_price}
                                        onChange={(e) =>
                                          updateTierInWindow(
                                            windowIndex,
                                            tierIndex,
                                            'track_price',
                                            e.target.value
                                          )
                                        }
                                      />
                                      <ColorSelect
                                        value={tier.color_code || ''}
                                        onChange={(value) =>
                                          updateTierInWindow(
                                            windowIndex,
                                            tierIndex,
                                            'color_code',
                                            value
                                          )
                                        }
                                        options={COLOR_CODES}
                                        placeholder="Select Color"
                                        allowEmpty
                                      />
                                      <RemoveTierButtonWrapper>
                                        <ButtonV2
                                          danger
                                          variant="icon"
                                          type="button"
                                          onClick={() =>
                                            removeTierFromWindow(
                                              windowIndex,
                                              tierIndex
                                            )
                                          }
                                          title="Remove tier"
                                          icon={<XIcon />}
                                        />
                                      </RemoveTierButtonWrapper>
                                    </TierRow>
                                  ))}
                                </>
                              )}
                            </TierSection>
                          )}
                        </SalesWindowCard>
                      ))
                    )}

                    <AddWindowButtonWrapper>
                      <ButtonV2
                        type="button"
                        onClick={addSalesWindow}
                        icon={<PlusIcon />}
                      >
                        Add Sales Window
                      </ButtonV2>
                    </AddWindowButtonWrapper>
                  </SalesWindowsContainer>
                </>
              )}

              {/* Step Navigation */}
              <StepNavigation>
                <NavButtonWrapper>
                  <ButtonV2
                    type="button"
                    onClick={handlePrevStep}
                    icon={<ArrowLeftIcon />}
                  >
                    Back
                  </ButtonV2>
                </NavButtonWrapper>
                <NavButtonWrapper>
                  <ButtonV2 elevated type="button" onClick={handleNextStep}>
                    {isBundle
                      ? isMobile
                        ? 'Continue'
                        : 'Continue to Select Tickets'
                      : isMobile
                      ? 'Continue'
                      : 'Continue to Options'}
                    <ArrowRightIcon />
                  </ButtonV2>
                </NavButtonWrapper>
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}

        {/* Step 3: Select Tickets (Bundle only) */}
        {currentStep === 3 && isBundle && (
          <FormCard>
            <FormSection>
              <SectionTitle>Select Tickets for Bundle</SectionTitle>

              <BundleTicketsSection
                availableTickets={availableTicketsForBundle}
                selectedTickets={formData.associated_tickets}
                onChange={(newAssociatedTickets) =>
                  handleChange('associated_tickets', newAssociatedTickets)
                }
              />

              {errors.associated_tickets && (
                <ErrorText style={{ marginTop: '16px' }}>
                  {errors.associated_tickets}
                </ErrorText>
              )}

              {/* Step Navigation */}
              <StepNavigation>
                <NavButtonWrapper>
                  <ButtonV2
                    type="button"
                    onClick={handlePrevStep}
                    icon={<ArrowLeftIcon />}
                  >
                    Back
                  </ButtonV2>
                </NavButtonWrapper>
                <NavButtonWrapper>
                  <ButtonV2 elevated type="button" onClick={handleNextStep}>
                    {isMobile ? 'Continue' : 'Continue to Options'}
                    <ArrowRightIcon />
                  </ButtonV2>
                </NavButtonWrapper>
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}

        {/* Step 3: Additional Options (non-bundle) / Step 4: Additional Options (bundle) */}
        {((currentStep === 3 && !isBundle) ||
          (currentStep === 4 && isBundle)) && (
          <FormCard>
            <FormSection>
              <SectionTitle>Additional Options</SectionTitle>

              <FormRow columns="1fr 1fr 1fr">
                <FormFieldSelect
                  label="Visibility"
                  value={formData.visibility}
                  onChange={(e) => handleChange('visibility', e.target.value)}
                  options={VISIBILITY_OPTIONS}
                />

                <FormGroup>
                  <CategorySelect
                    value={formData.category_id}
                    onChange={(option) =>
                      handleChange('category_id', option?.value || '')
                    }
                    onSuccess={(name) =>
                      setToaster({
                        isOpen: true,
                        type: 'success',
                        title: 'Category Created',
                        message: `"${name}" has been added.`,
                      })
                    }
                    onError={() =>
                      setToaster({
                        isOpen: true,
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to create category. Please try again.',
                      })
                    }
                  />
                  {errors.category_id && (
                    <ErrorText>{errors.category_id}</ErrorText>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Ticket Limit</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="No limit"
                    value={formData.limit}
                    onChange={(e) => handleChange('limit', e.target.value)}
                  />
                </FormGroup>
              </FormRow>

              {/* Featured Ticket */}
              <ToggleRow style={{ marginTop: '16px' }}>
                <ToggleInfo>
                  <ToggleTitle>Featured Ticket</ToggleTitle>
                  <ToggleDescription>
                    Highlight this ticket with custom text
                  </ToggleDescription>
                </ToggleInfo>
                <Toggle
                  checked={formData.is_featured}
                  onChange={(e) =>
                    handleChange('is_featured', e.target.checked)
                  }
                />
              </ToggleRow>

              {formData.is_featured && (
                <FormGroup style={{ marginTop: '16px' }}>
                  <Label>Featured Text</Label>
                  <Input
                    type="text"
                    placeholder="e.g., Best Value!"
                    value={formData.featured_text}
                    onChange={(e) =>
                      handleChange('featured_text', e.target.value)
                    }
                  />
                </FormGroup>
              )}

              {/* Reserved Seating Options */}
              {formData.reserved_seat && (
                <>
                  <SectionTitle style={{ marginTop: '24px' }}>
                    Reserved Seating Settings
                  </SectionTitle>

                  <FormRow columns="1fr 1fr">
                    <FormGroup>
                      <Label>Seats.io Category</Label>
                      <Input
                        type="text"
                        placeholder="Enter category"
                        value={formData.reserved_seat_category}
                        onChange={(e) =>
                          handleChange('reserved_seat_category', e.target.value)
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Ticket Prefix</Label>
                      <Input
                        type="text"
                        placeholder="Enter prefix"
                        value={formData.reserved_seat_prefix}
                        onChange={(e) =>
                          handleChange('reserved_seat_prefix', e.target.value)
                        }
                      />
                    </FormGroup>
                  </FormRow>

                  <TwoColumnGrid style={{ marginTop: '16px', marginBottom: 0 }}>
                    <FormGroup>
                      <Label>Seating Chart</Label>
                      <SearchableSelect
                        value={formData.seating_chart_id}
                        onChange={(option) =>
                          handleChange(
                            'seating_chart_id',
                            option?.value || null
                          )
                        }
                        options={seatingChartOptions}
                        loading={chartsLoading}
                        placeholder="Select a seating chart..."
                        isClearable
                      />
                    </FormGroup>

                    <ToggleRow>
                      <ToggleInfo>
                        <ToggleTitle>Show as Section/Row/Seat</ToggleTitle>
                        <ToggleDescription>
                          Display seat information in section, row, seat format
                        </ToggleDescription>
                      </ToggleInfo>
                      <Toggle
                        checked={formData.reserved_seat_section}
                        onChange={(e) =>
                          handleChange(
                            'reserved_seat_section',
                            e.target.checked
                          )
                        }
                      />
                    </ToggleRow>
                  </TwoColumnGrid>
                </>
              )}

              {/* Member Numbers - Only for registration types */}
              {isRegistration && (
                <>
                  <SectionTitle style={{ marginTop: '24px' }}>
                    Member Numbers
                  </SectionTitle>
                  <SectionDescription>
                    Configure automatic member number assignment for purchasers
                  </SectionDescription>

                  <ToggleRow>
                    <ToggleInfo>
                      <ToggleTitle>Set Member Numbers</ToggleTitle>
                      <ToggleDescription>
                        Automatically assign member numbers to purchasers
                      </ToggleDescription>
                    </ToggleInfo>
                    <Toggle
                      checked={formData.set_member_number}
                      onChange={(e) =>
                        handleChange('set_member_number', e.target.checked)
                      }
                    />
                  </ToggleRow>

                  {formData.set_member_number && (
                    <>
                      <FormRow columns="1fr 1fr">
                        <FormGroup>
                          <LabelWithTooltip>
                            Prefix Text
                            <Tooltip
                              info
                              content="Text that appears before the member number. For example, entering '2026' would produce member numbers like '2026-1'."
                              iconColor={infoIconColor}
                              iconSize="16px"
                            />
                          </LabelWithTooltip>
                          <Input
                            type="text"
                            value={formData.prefix_text}
                            onChange={(e) =>
                              handleChange('prefix_text', e.target.value)
                            }
                            placeholder="e.g., 2026"
                          />
                        </FormGroup>

                        <FormGroup>
                          <LabelWithTooltip>
                            Suffix Text
                            <Tooltip
                              info
                              content="Text that appears after the member number. For example, entering 'D' would produce member numbers like '1-D'."
                              iconColor={infoIconColor}
                              iconSize="16px"
                            />
                          </LabelWithTooltip>
                          <Input
                            type="text"
                            value={formData.suffix_text}
                            onChange={(e) =>
                              handleChange('suffix_text', e.target.value)
                            }
                            placeholder="e.g., D"
                          />
                        </FormGroup>
                      </FormRow>

                      <FormRow>
                        <FormGroup>
                          <LabelWithTooltip>
                            Starting Member Number
                            <Tooltip
                              info
                              content="The next member number to be assigned. Numbers will increment from this value."
                              iconColor={infoIconColor}
                              iconSize="16px"
                            />
                          </LabelWithTooltip>
                          <Input
                            type="number"
                            value={formData.last_member_number}
                            onChange={(e) =>
                              handleChange('last_member_number', e.target.value)
                            }
                            placeholder="1"
                            min="1"
                          />
                        </FormGroup>
                      </FormRow>

                      <SectionDescription style={{ marginTop: '16px' }}>
                        <strong>Preview:</strong>{' '}
                        {[
                          formData.prefix_text,
                          formData.last_member_number || '1',
                          formData.suffix_text,
                        ]
                          .filter(Boolean)
                          .join('-')}
                      </SectionDescription>
                    </>
                  )}
                </>
              )}

              {/* Step Navigation */}
              <StepNavigation>
                <NavButtonWrapper>
                  <ButtonV2
                    type="button"
                    onClick={handlePrevStep}
                    icon={<ArrowLeftIcon />}
                  >
                    Back
                  </ButtonV2>
                </NavButtonWrapper>
                {isRegistration ? (
                  <NavButtonWrapper>
                    <ButtonV2 elevated type="button" onClick={handleNextStep}>
                      {isMobile ? 'Continue' : 'Continue to Pages'}
                      <ArrowRightIcon />
                    </ButtonV2>
                  </NavButtonWrapper>
                ) : (
                  <NavButtonWrapper>
                    <ButtonV2
                      elevated
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Adding...' : 'Add Ticket'}
                    </ButtonV2>
                  </NavButtonWrapper>
                )}
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}

        {/* Step 4: Registration Pages */}
        {currentStep === 4 && isRegistration && (
          <FormCard>
            <FormSection>
              <SectionTitle>Registration Pages</SectionTitle>
              <SectionDescription>
                Add pages that will be displayed during checkout. Pages can
                include PDFs, videos, forms, and waivers.
              </SectionDescription>

              <PagesContainer>
                <PagesListSection>
                  {formData.pages.length === 0 ? (
                    <EmptyPagesMessage>
                      No pages configured yet. Add a page to get started.
                    </EmptyPagesMessage>
                  ) : (
                    formData.pages.map((page, pageIndex) => (
                      <PageCard key={page.id}>
                        <PageCardHeader>
                          <PageCardTitle>
                            {page.name || `Page ${pageIndex + 1}`}
                          </PageCardTitle>
                          <DeletePageButtonWrapper>
                            <ButtonV2
                              danger
                              variant="icon"
                              type="button"
                              onClick={() => removePage(pageIndex)}
                              title="Remove page"
                              icon={<TrashIcon />}
                            />
                          </DeletePageButtonWrapper>
                        </PageCardHeader>

                        <FormRow columns="1fr">
                          <FormGroup>
                            <Label>Page Name</Label>
                            <Input
                              type="text"
                              placeholder="Enter page name"
                              value={page.name || ''}
                              onChange={(e) =>
                                updatePage(pageIndex, 'name', e.target.value)
                              }
                            />
                          </FormGroup>
                        </FormRow>

                        <FormGroup style={{ marginBottom: '16px' }}>
                          <Label>PDF Upload</Label>
                          <AttachmentUpload
                            attachments={
                              page.pdf_id &&
                              (page.pdf_url || page.pdf_upload_url)
                                ? [
                                    {
                                      id: page.pdf_id,
                                      name: 'Uploaded PDF',
                                      size: 0,
                                      sizeFormatted: '',
                                      url: page.pdf_url || page.pdf_upload_url,
                                      type: 'application/pdf',
                                      isPDF: true,
                                    },
                                  ]
                                : []
                            }
                            onAttachmentsChange={(attachments) => {
                              if (attachments.length > 0) {
                                updatePage(
                                  pageIndex,
                                  'pdf_id',
                                  attachments[0].id
                                );
                                updatePage(
                                  pageIndex,
                                  'pdf_url',
                                  attachments[0].url
                                );
                              } else {
                                updatePage(pageIndex, 'pdf_id', null);
                                updatePage(pageIndex, 'pdf_url', null);
                              }
                            }}
                            maxFiles={1}
                            maxFileSize={10485760}
                            channel="SMS"
                          />
                        </FormGroup>

                        <FormRow columns="1fr 1fr">
                          <FormGroup>
                            <Label>Video URL</Label>
                            <Input
                              type="url"
                              placeholder="https://..."
                              value={page.video_url || ''}
                              onChange={(e) =>
                                updatePage(
                                  pageIndex,
                                  'video_url',
                                  e.target.value
                                )
                              }
                            />
                          </FormGroup>

                          <FormGroup>
                            <Label>Form ID</Label>
                            <SearchableSelect
                              value={
                                page.form_id ? parseInt(page.form_id) : null
                              }
                              onChange={(option) => {
                                updatePage(
                                  pageIndex,
                                  'form_id',
                                  option?.value?.toString() || null
                                );
                              }}
                              options={formstackOptions}
                              loading={formstackLoading}
                              onSearch={handleFormstackSearch}
                              minSearchLength={4}
                              placeholder="Type to search forms..."
                              noResultsText="No forms found"
                            />
                          </FormGroup>
                        </FormRow>

                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label>Waivers</Label>
                          <WaiversContainer>
                            {(page.waivers || []).length === 0 ? (
                              <EmptyText>No waivers added</EmptyText>
                            ) : (
                              page.waivers.map((waiver, waiverIndex) => (
                                <WaiverRow key={waiverIndex}>
                                  <FormFieldSelect
                                    value={waiver.waiver || ''}
                                    onChange={(e) =>
                                      updateWaiverInPage(
                                        pageIndex,
                                        waiverIndex,
                                        e.target.value
                                      )
                                    }
                                    placeholder="Select a waiver..."
                                    options={waivers.map((w) => ({
                                      label: w.name,
                                      value: w.id,
                                    }))}
                                  />
                                  <RemoveWaiverButtonWrapper>
                                    <ButtonV2
                                      danger
                                      variant="icon"
                                      type="button"
                                      onClick={() =>
                                        removeWaiverFromPage(
                                          pageIndex,
                                          waiverIndex
                                        )
                                      }
                                      title="Remove waiver"
                                      icon={<MinusIcon />}
                                    />
                                  </RemoveWaiverButtonWrapper>
                                </WaiverRow>
                              ))
                            )}
                            <AddWaiverButtonWrapper>
                              <ButtonV2
                                elevated
                                type="button"
                                onClick={() => addWaiverToPage(pageIndex)}
                                icon={<PlusIcon />}
                              >
                                Add another waiver
                              </ButtonV2>
                            </AddWaiverButtonWrapper>
                          </WaiversContainer>
                        </FormGroup>
                      </PageCard>
                    ))
                  )}

                  <AddPageButtonWrapper>
                    <ButtonV2
                      elevated
                      type="button"
                      onClick={addPage}
                      icon={<PlusIcon />}
                    >
                      {formData.pages.length > 0
                        ? 'ADD ANOTHER PAGE'
                        : 'ADD PAGE'}
                    </ButtonV2>
                  </AddPageButtonWrapper>
                </PagesListSection>

                {formData.pages_with_order.length > 0 && (
                  <PagesOrderSection>
                    <OrderSectionTitle>Pages Order In App</OrderSectionTitle>
                    <ReactSortable
                      list={getOrderedItems(formData.pages_with_order)}
                      setList={handlePagesSort}
                    >
                      {getOrderedItems(formData.pages_with_order).map(
                        (page) => (
                          <DraggablePageItem key={page.id || page.order}>
                            <DragHandle>
                              <DragHandleIcon />
                            </DragHandle>
                            <PageOrderName>
                              {page.name || 'Unnamed Page'}
                            </PageOrderName>
                          </DraggablePageItem>
                        )
                      )}
                    </ReactSortable>
                  </PagesOrderSection>
                )}
              </PagesContainer>

              {/* Step Navigation */}
              <StepNavigation>
                <NavButtonWrapper>
                  <ButtonV2
                    type="button"
                    onClick={handlePrevStep}
                    icon={<ArrowLeftIcon />}
                  >
                    Back
                  </ButtonV2>
                </NavButtonWrapper>
                <NavButtonWrapper>
                  <ButtonV2
                    elevated
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Adding...' : 'Add Ticket'}
                  </ButtonV2>
                </NavButtonWrapper>
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}
      </Container>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => {
          setToaster({ isOpen: false });
          if (toaster.navigateOnClose) {
            handleBack();
          }
        }}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
        action={toaster.action}
      />
    </PageWrapper>
  );
};

// Main component wrapped with theme provider
const AddEventTicket = () => {
  return (
    <EnhancedThemeProvider>
      <AddEventTicketContent />
    </EnhancedThemeProvider>
  );
};

export default AddEventTicket;
